
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const PreisePage = () => {
  const [selectedPlan, setSelectedPlan] = useState<'eco' | 'premium'>('premium');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    version: 'premium',
    nachricht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const features = [
    { name: 'Kundenverwaltung', eco: true, premium: true },
    { name: 'Auftragsverwaltung', eco: true, premium: true },
    { name: 'Artikelverwaltung', eco: true, premium: true },
    { name: 'LV-System', eco: true, premium: true },
    { name: 'Zahlungsüberwachung', eco: true, premium: true },
    { name: 'Brieftext/Rundschreiben', eco: true, premium: true },
    { name: 'Datensicherung', eco: true, premium: true },
    { name: 'Handbuch und Hilfe', eco: true, premium: true },
    { name: 'Basis-Aufmaß', eco: false, premium: true },
    { name: 'Aufmaß-Erweiterung', eco: false, premium: true },
    { name: 'Vorkalkulation', eco: false, premium: true },
    { name: 'Nachkalkulation', eco: false, premium: true },
    { name: 'E-Mail Versand', eco: false, premium: true },
    { name: 'XRechnung', eco: false, premium: true },
    { name: 'Bauzeit Pro', eco: false, premium: true },
    { name: 'Preisspiegel', eco: false, premium: true },
    { name: 'Tagesberichte', eco: false, premium: true },
    { name: 'Lagerverwaltung', eco: false, premium: true },
    { name: 'Jahresverträge', eco: false, premium: true },
    { name: 'Lieferscheinaufstellung', eco: false, premium: true },
    { name: 'Mandanten-Installation', eco: false, premium: true }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3osfnrfiitv34rgaorh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        setSubmitMessage('Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.');
        setFormData({ name: '', email: '', telefon: '', version: 'premium', nachricht: '' });
      } else {
        setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      setSubmitMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-lightBlue to-primary-100 py-20 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/57120a884b7825a31eccf2ec0d496454.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Preise & <span className="text-primary-600">Pakete</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Wählen Sie das passende Baufaktura Paket für Ihr Handwerksunternehmen. 
              Alle Preise verstehen sich zzgl. MwSt.
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                30 Tage kostenlos testen
              </div>
              <div className="flex items-center">
                <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                Keine Einrichtungsgebühr
              </div>
              <div className="flex items-center">
                <i className="ri-check-circle-fill text-green-500 mr-2"></i>
                Support inklusive
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* ECO Version */}
            <div className="bg-white border-2 border-lightGrey rounded-3xl p-8 lg:p-12 relative hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-lightBlue rounded-xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-seedling-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">baufaktura ECO</h3>
                <p className="text-gray-600 mb-6">Grundausstattung für Einsteiger</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-primary-600">299€</span>
                  <span className="text-gray-600 ml-2">einmalig</span>
                </div>
                <Button 
                  onClick={() => {
                    setSelectedPlan('eco');
                    setFormData({...formData, version: 'eco'});
                  }}
                  variant="outline"
                  fullWidth
                  size="lg"
                  className="mb-4"
                >
                  ECO Version wählen
                </Button>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 mb-4 text-lg">Enthaltene Funktionen:</h4>
                {features.filter(f => f.eco).map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-gray-700">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* PREMIUM Version */}
            <div className="bg-gradient-to-br from-lightBlue to-primary-100 border-2 border-primary-300 rounded-3xl p-8 lg:p-12 relative hover:shadow-xl transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                  Empfohlen
                </span>
              </div>
              
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-vip-crown-line text-2xl text-white"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">baufaktura PREMIUM</h3>
                <p className="text-gray-600 mb-6">Vollausstattung für Profis</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-primary-600">695€</span>
                  <span className="text-gray-600 ml-2">einmalig</span>
                </div>
                <Button 
                  onClick={() => {
                    setSelectedPlan('premium');
                    setFormData({...formData, version: 'premium'});
                  }}
                  fullWidth
                  size="lg"
                  className="mb-4"
                >
                  PREMIUM Version wählen
                </Button>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 mb-4 text-lg">Alle ECO Funktionen plus:</h4>
                {features.filter(f => f.premium && !f.eco).map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-gray-700">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section className="py-20 bg-lightGrey">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Jetzt bestellen oder testen
            </h2>
            <p className="text-xl text-gray-600">
              Starten Sie noch heute mit Ihrer 30-tägigen kostenlosen Testversion 
              oder bestellen Sie direkt Ihre Lizenz.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
            <form onSubmit={handleSubmit} data-readdy-form id="version-bestellung">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="+49 123 456 789"
                  />
                </div>
                <div>
                  <label htmlFor="version" className="block text-sm font-medium text-gray-700 mb-2">
                    Gewünschte Version *
                  </label>
                  <div className="relative">
                    <select
                      id="version"
                      name="version"
                      required
                      value={formData.version}
                      onChange={(e) => setFormData({...formData, version: e.target.value})}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="eco">baufaktura ECO (299€)</option>
                      <option value="premium">baufaktura PREMIUM (695€)</option>
                      <option value="eco-remote">baufaktura ECO Remote (29€/Monat)</option>
                      <option value="premium-remote">baufaktura PREMIUM Remote (49€/Monat)</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="nachricht" className="block text-sm font-medium text-gray-700 mb-2">
                  Nachricht
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  rows={4}
                  maxLength={500}
                  value={formData.nachricht}
                  onChange={(e) => setFormData({...formData, nachricht: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="Haben Sie spezielle Anforderungen oder Fragen?"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.nachricht.length}/500 Zeichen
                </div>
              </div>
              <Button 
                type="submit" 
                fullWidth 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Jetzt bestellen / testen
                  </>
                )}
              </Button>
              {submitMessage && (
                <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('erfolgreich') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Wichtige Informationen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles was Sie über unsere Preise und Leistungen wissen müssen.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-lightBlue rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-primary-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Einmaliger Kauf</h3>
              <p className="text-gray-600">
                Keine monatlichen Gebühren bei der Desktop-Version. 
                Einmal kaufen, dauerhaft nutzen.
              </p>
            </div>
            
            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-download-cloud-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Kostenlose Updates</h3>
              <p className="text-gray-600">
                Regelmäßige Updates und Verbesserungen 
                sind bei allen Versionen inklusive.
              </p>
            </div>
            
            <div className="text-center p-8 bg-lightGrey rounded-2xl">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-line text-primary-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Support inklusive</h3>
              <p className="text-gray-600">
                Umfassender Support und Hilfebereich 
                sind bei allen Paketen verfügbar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Remote Version */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              baufaktura Remote
            </h2>
            <p className="text-xl text-primary-200 max-w-3xl mx-auto">
              Nutzen Sie baufaktura von überall aus - ohne Installation auf Ihrem Computer.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mb-8">
                <i className="ri-cloud-line text-3xl"></i>
              </div>
              
              <h3 className="text-3xl font-bold mb-6">
                Cloud-basierte Lösung
              </h3>
              <p className="text-xl text-primary-100 mb-8">
                Arbeiten Sie von jedem Gerät mit Internetverbindung. 
                Ihre Daten sind sicher in der Cloud gespeichert.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-4 text-lg">Vorteile:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-400 mr-3"></i>
                      <span className="text-primary-100">Keine Installation erforderlich</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-400 mr-3"></i>
                      <span className="text-primary-100">Automatische Backups</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-400 mr-3"></i>
                      <span className="text-primary-100">Zugriff von überall</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-400 mr-3"></i>
                      <span className="text-primary-100">Immer aktuell</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white mb-4 text-lg">Preise:</h4>
                  <div className="space-y-4">
                    <div className="bg-primary-500 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-primary-100">ECO Remote:</span>
                        <span className="font-bold text-xl">29€/Monat</span>
                      </div>
                    </div>
                    <div className="bg-primary-500 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-primary-100">PREMIUM Remote:</span>
                        <span className="font-bold text-xl">49€/Monat</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cloud%20computing%20concept%20with%20laptop%20showing%20construction%20management%20software%20interface%2C%20floating%20cloud%20icons%20and%20data%20streams%2C%20professional%20workspace%20with%20blueprints%2C%20bright%20clean%20environment%2C%20technology%20and%20construction%20fusion&width=600&height=500&seq=cloud-software&orientation=landscape"
                alt="Cloud-basierte Baufaktura Software"
                className="w-full max-w-lg rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-lightGrey">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Häufige Fragen
            </h2>
            <p className="text-xl text-gray-600">
              Die wichtigsten Antworten zu unseren Preisen und Paketen.
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Welche Version ist für mich geeignet?
              </h3>
              <p className="text-gray-600">
                Die ECO Version eignet sich für kleinere Handwerksbetriebe mit grundlegenden Anforderungen. 
                Die PREMIUM Version bietet erweiterte Funktionen für professionelle Betriebe mit komplexeren Prozessen.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Kann ich später von ECO auf PREMIUM upgraden?
              </h3>
              <p className="text-gray-600">
                Ja, ein Upgrade ist jederzeit möglich. Sie zahlen nur die Differenz zwischen den Versionen.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Gibt es eine Testversion?
              </h3>
              <p className="text-gray-600">
                Ja, beide Versionen können 30 Tage kostenlos getestet werden. 
                Kontaktieren Sie uns für Ihren Testzugang.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Was ist der Unterschied zwischen Desktop und Remote Version?
              </h3>
              <p className="text-gray-600">
                Die Desktop-Version wird lokal auf Ihrem Computer installiert (einmalige Kosten), 
                während die Remote-Version über den Browser läuft und monatlich abgerechnet wird.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PreisePage;
